import {
    Component,
    OnInit,
    Output,
    EventEmitter,
    Input,
    ViewChild,
    ElementRef,
    AfterViewInit,
    HostBinding,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { format } from 'date-fns';
import { Store } from 'src/app/store';
import { newUUID } from 'src/app/utils/utils';
import { IOneDayMessageGroup } from '../components/chat-day-message-block/chat-day-message-block.component';
import { ChatComponent, IChatOpened } from '../chat.component';
import { EChatEventName, EChatType } from '../enums';
import { IChat, IChatEvent, IChatMember, IChatMessage } from '../models';
import { ChatEventsService } from '../services/chat-events.service';
import { ChatRESTService } from '../services/chat-rest.service';
import { ChatSocketService } from '../services/chat-socket.service';
import { UserService } from 'src/app/services/user.service';
import { Router } from '@angular/router';

@UntilDestroy()
@Component({
    selector: 'app-chat-modal',
    templateUrl: './chat-modal.component.html',
    styleUrls: ['./chat-modal.component.scss'],
})
export class ChatModalComponent implements OnInit, AfterViewInit {
    @Input() recipient: IChatMember;
    @Input() chatId: number;
    @Input() chatName: string = null;
    @Input() orderId: string = null;
    @Input() styleClass: string = '';
    @Input() chatType = EChatType.Order;
    @Input() advancedMode = false;
    @Output() close = new EventEmitter<void>();
    @ViewChild('scrollPanel') scrollPanel: ElementRef;
    @HostBinding('class')
    get classes(): Record<string, boolean> {
        return {
            [this.styleClass]: true,
        };
    }

    openedChat: IChatOpened = {
        ...ChatComponent.OPENED_CHAT_DEFAULTS,
        id: null,
    };
    content = new FormControl();

    user = this.store.selectForLocal('user');

    loadingChat = false;

    messagesGroups: IOneDayMessageGroup[];

    get sendAllowed(): boolean {
        return this.content.value?.trim().length > 0;
    }

    constructor(
        private store: Store,
        private chatSocketService: ChatSocketService,
        private chatEventsService: ChatEventsService,
        private chatRESTService: ChatRESTService,
        private userService: UserService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.loadChatById();
        this.setListenersForMessageEvents();
        this.openedChat.members = [this.recipient];
    }

    ngAfterViewInit() {
        this.scrollMesagesDown();
    }

    onCloseClick() {
        this.close.emit();
    }

    send() {
        const content = this.content.value;
        const messageForSend = this.buildMessage(content);
        this.openedChat.sendingMessageId = messageForSend.id;
        this.chatRESTService.sendMessage(messageForSend).subscribe(() => {
            this.openedChat.isClosed = 0;
        });
        this.cleanEditor();
    }

    enlargeChat() {
        if (this.openedChat.id) {
            this.router.navigate(['chat', this.openedChat.id]);
        } else {
            this.router.navigate(['chat'], { queryParams: { 'preselected-user': this.recipient.id } });
        }
    }

    get isOnline() {
        return this.userService.userOnline(this.recipient.id);
    }

    private buildMessage(editorData: string): IChatMessage {
        const domain = this.store.selectForLocal('domain');

        const sender: IChatMember = {
            id: this.user.id,
            firstname: this.user.contact.firstname,
            surname: this.user.contact.surname,
            avatarColour: this.user.avatarColour,
            accountType: this.user.accountType,
        };

        // #TODO - need to handle recipients on Server Side
        const recipients = [this.recipient];

        return {
            id: newUUID(),
            content: editorData,
            chatId: this.openedChat.id,
            user: sender,
            createdAt: new Date(),
            domainId: domain,
            channelName: this.chatName,
            recipients: recipients,
            type: 'chat',
            chatType: this.chatType,
            relatedEntityId: this.orderId,
        };
    }

    getFormattedDate(date: string) {
        return format(new Date(date), 'do MMMM yyyy');
    }

    private cleanEditor() {
        this.content.setValue(null);
    }

    private loadChatById(): void {
        if (this.chatId) {
            this.loadingChat = true;
            this.chatRESTService
                .getChat(this.chatId)
                .pipe(untilDestroyed(this))
                .subscribe((chat: IChat) => {
                    this.openedChat = chat;
                    this.messagesGroups = this.groupByDays(this.openedChat.messages);
                    this.loadingChat = false;
                });
        }
    }

    private setListenersForMessageEvents(): void {
        this.chatEventsService
            .listenEvent(EChatEventName.MessageAdded)
            .pipe(untilDestroyed(this))
            .subscribe((event: IChatEvent) => {
                const message: IChatMessage = event.data;

                if (message.id === this.openedChat.sendingMessageId) {
                    // handle case for first (initialization) message in chat
                    if (this.openedChat.id === null && message.chatId) {
                        this.openedChat.id = message.chatId;
                    }
                    this.openedChat.sendingMessageId = null;
                } else if (message.chatId !== this.openedChat.id) {
                    return;
                }

                this.openedChat.messages.push(message);
                this.messagesGroups = this.groupByDays(this.openedChat.messages);
                this.scrollMesagesDown();
            });
    }

    /**
     * Grouping chat message in one-day blocks
     */
    private groupByDays(chat: IChatMessage[]): IOneDayMessageGroup[] {
        let dayMessageBlocks: IOneDayMessageGroup[] = [];
        let messages = chat.map((m) => {
            return { ...m, day: new Date(m.createdAt).toISOString().split('T')[0] };
        });

        messages = messages.sort((a: IChatMessage, b: IChatMessage) => {
            return a.createdAt.getTime() - b.createdAt.getTime();
        });

        dayMessageBlocks = messages.reduce(function (rv, x) {
            (rv[x['day']] = rv[x['day']] || []).push(x);
            return rv;
        }, []);

        return dayMessageBlocks;
    }

    private scrollMesagesDown() {
        setTimeout(() => {
            this.scrollPanel.nativeElement.scrollTop = this.scrollPanel.nativeElement.scrollHeight;
        }, 300);
    }
}
